import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { GraduationCap, Search, FileSpreadsheet, Loader2, Check, X, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const Yes = ({ ok, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${ok ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-400"}`}>
    {ok ? <Check size={14} /> : <X size={14} />}
  </span>
);

const StudentsAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [season, setSeason] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/students-directory");
      setRows(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seasons = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => (r.seasons || []).forEach((x) => s.add(x)));
    return Array.from(s).sort().reverse();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (season && !(r.seasons || []).includes(season)) return false;
      if (!needle) return true;
      return [r.firstName, r.lastName, r.parentName, r.email, r.phone, r.city, (r.courses || []).join(" ")]
        .join(" ").toLowerCase().includes(needle);
    });
  }, [rows, q, season]);

  const exportXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/students-directory/export${season ? `?season=${encodeURIComponent(season)}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      a.download = `elan-danse-eleves-${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) { toast.error("Erreur lors de l'export : " + (e?.response?.data?.detail || e.message)); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="students-admin">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800 flex items-center gap-2"><GraduationCap size={24} /> Élèves</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Liste alphabétique de tous les élèves avec leurs informations, leurs cours et les consentements (règlement, droit à l'image).</p>
        </div>
        <button onClick={exportXlsx} disabled={downloading} className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-full text-sm disabled:opacity-50 shrink-0" data-testid="students-export-btn">
          {downloading ? <Loader2 className="animate-spin" size={15} /> : <FileSpreadsheet size={15} />} Export Excel
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un élève, un parent, un cours…" data-testid="students-search" className="w-full bg-stone-50 border border-stone-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
          <select value={season} onChange={(e) => setSeason(e.target.value)} data-testid="students-season-filter" className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush">
            <option value="">Toutes les saisons</option>
            {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="text-sm text-stone-500 mb-3" data-testid="students-count">{filtered.length} élève(s)</div>

        {loading ? (
          <div className="py-10 text-center text-stone-400"><Loader2 className="animate-spin inline" size={18} /></div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="students-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-stone-500 border-b border-stone-200">
                    <th className="py-2 pr-3">Élève</th>
                    <th className="py-2 pr-3">Naissance / Âge</th>
                    <th className="py-2 pr-3">Cours</th>
                    <th className="py-2 pr-3">Responsable</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Adresse</th>
                    <th className="py-2 pr-2 text-center">Règl.</th>
                    <th className="py-2 pr-2 text-center">CGV</th>
                    <th className="py-2 pr-2 text-center">Image</th>
                    <th className="py-2 pr-3">Accepté le</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/60 align-top" data-testid={`students-row-${r.id}`}>
                      <td className="py-2.5 pr-3 whitespace-nowrap">
                        <div className="text-stone-800 font-medium">{(r.lastName || "").toUpperCase()} {r.firstName}</div>
                        {r.notes ? <div className="text-xs text-stone-400 max-w-[180px] truncate" title={r.notes}>{r.notes}</div> : null}
                      </td>
                      <td className="py-2.5 pr-3 text-stone-600 whitespace-nowrap">{r.birthDate || "—"}{r.age ? ` · ${r.age} ans` : ""}</td>
                      <td className="py-2.5 pr-3 text-stone-600 max-w-[260px]">{r.courses?.length ? r.courses.join(", ") : <span className="text-stone-400 italic">aucun</span>}</td>
                      <td className="py-2.5 pr-3 text-stone-600 whitespace-nowrap">{r.parentName || "—"}</td>
                      <td className="py-2.5 pr-3 text-stone-600">
                        {r.email ? <a href={`mailto:${r.email}`} className="text-deep-rose block">{r.email}</a> : "—"}
                        <span className="text-xs text-stone-500">{r.phone}</span>
                      </td>
                      <td className="py-2.5 pr-3 text-stone-600 max-w-[200px]">{[r.address, r.postalCode, r.city].filter(Boolean).join(", ") || "—"}</td>
                      <td className="py-2.5 pr-2 text-center"><Yes ok={r.consentReglement} testId={`students-reglement-${r.id}`} /></td>
                      <td className="py-2.5 pr-2 text-center"><Yes ok={r.consentCgv} /></td>
                      <td className="py-2.5 pr-2 text-center"><Yes ok={r.consentImage} testId={`students-image-${r.id}`} /></td>
                      <td className="py-2.5 pr-3 text-stone-500 text-xs whitespace-nowrap">{r.consentAcceptedAt ? new Date(r.consentAcceptedAt).toLocaleDateString("fr-FR") : "—"}</td>
                      <td className="py-2.5">
                        {r.familyId ? <Link to={`/admin/families?family=${r.familyId}`} className="text-deep-rose inline-flex items-center text-xs" data-testid={`students-family-link-${r.id}`}>Famille <ChevronRight size={13} /></Link> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden space-y-2.5">
              {filtered.map((r) => (
                <div key={r.id} className="border border-stone-200 rounded-xl p-3" data-testid={`students-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-stone-800 font-medium">{(r.lastName || "").toUpperCase()} {r.firstName}</div>
                      <div className="text-xs text-stone-500">{r.birthDate || "—"}{r.age ? ` · ${r.age} ans` : ""}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <div className="text-center"><Yes ok={r.consentReglement} /><div className="text-[10px] text-stone-400 mt-0.5">Règl.</div></div>
                      <div className="text-center"><Yes ok={r.consentImage} /><div className="text-[10px] text-stone-400 mt-0.5">Image</div></div>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-stone-600">{r.courses?.length ? r.courses.join(", ") : <span className="text-stone-400 italic">aucun cours</span>}</div>
                  <div className="mt-2 text-xs text-stone-500 space-y-0.5">
                    <div>{r.parentName}</div>
                    {r.email ? <a href={`mailto:${r.email}`} className="text-deep-rose block">{r.email}</a> : null}
                    {r.phone ? <a href={`tel:${r.phone}`} className="text-deep-rose block">{r.phone}</a> : null}
                    <div>{[r.address, r.postalCode, r.city].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              ))}
            </div>

            {filtered.length === 0 && <div className="py-8 text-center text-stone-400 italic text-sm">Aucun élève trouvé.</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentsAdmin;
